import React, { useState, useEffect } from 'react';
import {
  TextField,
  MenuItem,
  Stack
} from '@mui/material';
import Modal from './Modal';
import FormGroup from './FormGroup';
import Button from './Button';
import Alert from './Alert';
import api from '../services/api';

// Attribute types from domain_models.py - values must match backend enum exactly
const ATTRIBUTE_TYPES = [
  // Extract attributes
  { value: 'extract.imageUrl', label: 'Image URL' },
  { value: 'extract.instructions', label: 'Instructions' },
  { value: 'extract.promotionText', label: 'Promotion Text' },
  { value: 'extract.outOfStock', label: 'Out of Stock' },
  { value: 'extract.ingredients', label: 'Ingredients' },
  { value: 'extract.price', label: 'Price' },
  { value: 'extract.description', label: 'Description' },
  { value: 'extract.manufacturer', label: 'Manufacturer' },
  { value: 'extract.seller', label: 'Seller' },
  { value: 'extract.hasRichContent', label: 'Has Rich Content' },
  { value: 'extract.name', label: 'Name' },
  { value: 'extract.variant', label: 'Variant' },
  { value: 'extract.groupId', label: 'Group ID' },
  { value: 'extract.gtin', label: 'GTIN' },
  { value: 'extract.productNo', label: 'Product Number' },
  { value: 'extract.pricePerUnit', label: 'Price Per Unit' },
  { value: 'extract.netQuantity', label: 'Net Quantity' },
  
  // Nutrition attributes
  { value: 'extract.allergens', label: 'Allergens' },
  { value: 'extract.nutrition', label: 'Nutrition' },
  { value: 'extract.salt', label: 'Salt' },
  { value: 'extract.fat', label: 'Fat' },
  { value: 'extract.sugar', label: 'Sugar' },
  { value: 'extract.fibre', label: 'Fibre' },
  { value: 'extract.protein', label: 'Protein' },
  { value: 'extract.carbohydrates', label: 'Carbohydrates' },
  { value: 'extract.calories', label: 'Calories' },
  
  // Product details
  { value: 'extract.brand', label: 'Brand' },
  { value: 'extract.country', label: 'Country' },
  { value: 'extract.storage', label: 'Storage' },
  { value: 'extract.abv', label: 'ABV (Alcohol By Volume)' },
  
  // Rich content
  { value: 'extract.summary', label: 'Summary' },
  { value: 'extract.features', label: 'Features' },
  { value: 'extract.featureImageUrl', label: 'Feature Image URL' },
  { value: 'extract.video', label: 'Video' },
  { value: 'extract.bulletPoints', label: 'Bullet Points' },
  
  // Seller information
  { value: 'extract.sellerType', label: 'Seller Type' },
  { value: 'extract.sellerRank', label: 'Seller Rank' },
  
  // Stock and availability
  { value: 'extract.stockCount', label: 'Stock Count' },
  { value: 'extract.sponsored', label: 'Sponsored' },
  
  // Subscription
  { value: 'extract.frequency', label: 'Frequency' },
  { value: 'extract.subscriptionPrice', label: 'Subscription Price' },
  { value: 'extract.subscription', label: 'Subscription' },
  
  // Metadata
  { value: 'extract.meta.keywords', label: 'Meta Keywords' },
  { value: 'extract.saveAnExtra', label: 'Save An Extra' },
  { value: 'extract.badge', label: 'Badge' },
  { value: 'extract.contentSpecifications', label: 'Content Specifications' },
  
  // Feedback attributes
  { value: 'feedback.avgRating', label: 'Average Rating' },
  { value: 'feedback.commentCount', label: 'Comment Count' },
  { value: 'feedback.recommendation', label: 'Recommendation' },
  { value: 'feedback.totalRatings', label: 'Total Ratings' },
  { value: 'feedback.totalRecommendations', label: 'Total Recommendations' },
  { value: 'feedback.summary', label: 'Feedback Summary' },
  
  // Context ratings
  { value: 'feedback.contextRatings.context', label: 'Context Ratings - Context' },
  { value: 'feedback.contextRatings.rating', label: 'Context Ratings - Rating' },
  
  // Comments
  { value: 'feedback.comments.content', label: 'Comments - Content' },
  { value: 'feedback.comments.dateLabel', label: 'Comments - Date Label' },
  { value: 'feedback.comments.rating', label: 'Comments - Rating' },
  { value: 'feedback.comments.subject', label: 'Comments - Subject' },
  { value: 'feedback.comments.timestamp', label: 'Comments - Timestamp' },
  { value: 'feedback.comments.username', label: 'Comments - Username' },
  { value: 'feedback.comments.reviewId', label: 'Comments - Review ID' },
];

const PAGE_TYPE_OPTIONS = [
  { value: 'detail', label: 'Detail Page' },
  { value: 'search', label: 'Search Page' },
  { value: 'both', label: 'Both (Detail + Search)' },
];

function CreateRuleModal({ isOpen, onClose, onRuleCreated }) {
  const [ruleScope, setRuleScope] = useState('');
  const [attributeType, setAttributeType] = useState('');
  const [pageTypeSelection, setPageTypeSelection] = useState('detail');
  const [retailer, setRetailer] = useState('');
  const [availableRetailers, setAvailableRetailers] = useState([]);
  const [ruleText, setRuleText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.getAnomalyFilters().then((data) => {
      setAvailableRetailers(data.retailers || []);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const pageTypes = pageTypeSelection === 'both'
        ? ['detail', 'search']
        : [pageTypeSelection];

      const ruleData = {
        rule_scope: ruleScope,
        rule_text: ruleText,
        attribute_type: ruleScope === 'attribute_specific' ? attributeType : null,
        retailer: retailer || null,
        priority: 0,
        page_types: pageTypes,
      };

      await api.createRule(ruleData);
      setSuccess(true);
      setTimeout(() => {
        onRuleCreated();
      }, 1000);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Rule">
      <form onSubmit={handleSubmit}>
        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">Rule created successfully!</Alert>}
        
        <FormGroup label="Rule Scope" required>
          <TextField
            select
            fullWidth
            value={ruleScope}
            onChange={(e) => {
              setRuleScope(e.target.value);
              if (e.target.value === 'global') {
                setAttributeType('');
              }
            }}
            required
          >
            <MenuItem value="">Select scope...</MenuItem>
            <MenuItem value="global">Global (applies to all attributes)</MenuItem>
            <MenuItem value="attribute_specific">Attribute-Specific</MenuItem>
          </TextField>
        </FormGroup>

        {ruleScope === 'attribute_specific' && (
          <FormGroup label="Attribute Type" required>
            <TextField
              select
              fullWidth
              value={attributeType}
              onChange={(e) => setAttributeType(e.target.value)}
              required
            >
              <MenuItem value="">Select attribute type...</MenuItem>
              {ATTRIBUTE_TYPES.map((attr) => (
                <MenuItem key={attr.value} value={attr.value}>
                  {attr.label}
                </MenuItem>
              ))}
            </TextField>
          </FormGroup>
        )}

        <FormGroup label="Page Type" required>
          <TextField
            select
            fullWidth
            value={pageTypeSelection}
            onChange={(e) => setPageTypeSelection(e.target.value)}
            required
          >
            {PAGE_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </FormGroup>

        {availableRetailers.length > 0 && (
          <FormGroup label="Retailer (optional — leave blank to apply to all retailers)">
            <TextField
              select
              fullWidth
              value={retailer}
              onChange={(e) => setRetailer(e.target.value)}
            >
              <MenuItem value="">All retailers</MenuItem>
              {availableRetailers.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
          </FormGroup>
        )}

        <FormGroup label="Rule Text" required>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={ruleText}
            onChange={(e) => setRuleText(e.target.value)}
            placeholder="Enter the rule..."
            required
          />
        </FormGroup>

        <Stack direction="row" spacing={2} justifyContent="flex-end" className="mt-6">
          <Button variant="outlined" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Rule'}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}

export default CreateRuleModal;
